import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import PageSpinner from "../_components/page-spinner/PageSpinner";
import { AppPropertiesContext } from "../contexts/app-properties.context";
import AuthenticationService from "../_services/AuthenticationService";
import NotFound from "../pages/not-found/NotFound";
import SessionExpiredModal from "../components/SessionExpiredModal";
import ToolLauncher from "../components/ToolLauncher/ToolLauncher";

import { ROUTES } from "./registry";
import { renderRoutes, ROUTE_TITLES, DocumentTitle } from "./RouteRenderer";

interface Props {}

/**
 * Paths reachable WITHOUT being logged in. Everything else is gated by
 * GlobalAuthGate below. Keep this list intentionally small — only add a path
 * here if the page is genuinely meant to render for anonymous visitors.
 * Matches the exact path or any nested sub-path (e.g. "/login/sso").
 */
const PUBLIC_PATHS = ["/login", "/forgot-password", "/unauthorized"];

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * App-wide auth gate. Sits above the router so that ANY route not on the
 * public allowlist redirects logged-out users to /unauthorized — instead of
 * rendering a half-broken page that fires 401s and pops the session-expired
 * modal. Per-route `auth: "jwt"` guards still apply on top of this.
 */
const GlobalAuthGate: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const location = useLocation();
  const currentUser = AuthenticationService.currentUserValue;

  // A `?token=...` deep-link is signed in by checkToken() before gating, so let
  // it through here (mirrors RequireAuth in RouteRenderer).
  const hasToken = !!new URLSearchParams(location.search).get("token");

  if (isPublicPath(location.pathname) || hasToken) {
    return children;
  }

  if (currentUser) {
    let expired = false;
    try {
      const { exp } = jwtDecode<JwtPayload>((currentUser as any).token);
      if (typeof exp === "number" && exp * 1000 <= Date.now()) {
        expired = true;
      }
    } catch {
      expired = true;
    }
    if (!expired) return children;
    AuthenticationService.logout();
  }

  const next = encodeURIComponent(location.pathname + location.search);
  return <Redirect to={`/unauthorized?reason=not-logged-in&next=${next}`} />;
};

export default function Routes(_props: Props): ReactElement {
  const { isLoading } = useContext(AppPropertiesContext);
  const [isSigning, setIsSigning] = useState(false);

  const signInWithToken = async (token: string) => {
    const loginWithoutServerCall = false;
    let user: JwtPayload = {};

    void AuthenticationService.currentUserValue;

    if (loginWithoutServerCall) {
      user = jwtDecode(token);
      delete (user as any).sub;
      delete (user as any).jti;
      delete (user as any).iat;
      (user as any)["token"] = token;
    } else {
      const loginResponse = await AuthenticationService.loginUserWithToken(token);
      user = loginResponse.data?.data;
    }

    AuthenticationService.getDeviceId();
    localStorage.setItem("currentUser", JSON.stringify(user));
    AuthenticationService.setCurrentUserValue(user);
  };

  const checkToken = useCallback(async () => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const urlSearchLength = new Set(urlParams.keys()).size;

    if (urlSearchLength) {
      const token = urlParams.get("token");
      if (token) {
        setIsSigning(true);
        await signInWithToken(token);
        urlParams.delete("token");
        window.location.replace(
          `${window.location.origin}${window.location.pathname}${
            urlSearchLength > 1 ? "?" : ""
          }${urlParams.toString()}`,
        );
      }
    }
  }, []);

  useEffect(() => {
    checkToken();
  }, [checkToken]);

  const titles = useMemo(() => ROUTE_TITLES(ROUTES), []);

  return isLoading || isSigning ? (
    <PageSpinner />
  ) : (
    <>
      <DocumentTitle titles={titles} />
      <SessionExpiredModal />
      <GlobalAuthGate>
        <Switch>
          {/* Rendered as direct children so <Switch> matches first-wins and the
              catch-all below can show a 404 for any unregistered path instead of
              a blank screen. */}
          {renderRoutes(ROUTES)}
          <Route component={NotFound} />
        </Switch>
      </GlobalAuthGate>
      {/* Global "Live Tools" FAB — self-gates to instructor-linked logins on
          non-public pages and renders on every route regardless of layout mode. */}
      <ToolLauncher />
    </>
  );
}
